import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { useGameStore, evaluatePassword, checkPwnedPassword, PASSWORD_MIN_LENGTH } from '@campus/core';
import { getTenantSlug } from '../../lib/tenant.js';
import { API_BASE } from '../../lib/apiBase.js';
import { LogoMark } from '../ds/index.js';
import { PasswordInput } from '../components/PasswordInput.js';

// Jauge de force — index = score zxcvbn (0-4), voir packages/core/src/utils/passwordPolicy.ts.
const STRENGTH_LABELS = ['Très faible', 'Faible', 'Moyen', 'Bon', 'Excellent'];
const STRENGTH_COLORS = ['#e05252', '#e0824f', '#e0c34f', '#7cb85c', '#4caf7d'];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-3) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border-strong)',
  background: 'var(--color-bg-raised)',
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-sm)',
  fontFamily: 'var(--font-body)',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color var(--transition-base)',
};

const btnStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  background: 'var(--color-primary)',
  color: '#fff',
  fontSize: 'var(--text-base)',
  fontFamily: 'var(--font-body)',
  fontWeight: 'var(--font-bold)' as unknown as number,
  cursor: 'pointer',
  transition: 'opacity var(--transition-base)',
};

const SETUP_ERROR_MESSAGES: Record<string, string> = {
  INVALID_OR_EXPIRED_TOKEN: 'Ce lien est invalide, expiré, ou a déjà été utilisé.',
  INVALID_PASSWORD: 'Mot de passe manquant.',
  WEAK_PASSWORD: 'Mot de passe trop faible - voir les critères ci-dessus.',
  PWNED_PASSWORD: 'Ce mot de passe apparaît dans des fuites de données connues - choisis-en un autre.',
};

/**
 * SetPasswordPage — création (invitation) ou réinitialisation de mot de
 * passe via un lien reçu par email (Brevo).
 *
 * URL : /#setPassword={token} (fragment, jamais envoyé au serveur — voir App.tsx)
 * GET /auth/password-setup/:token → email + nom du tenant.
 * POST /auth/password-setup → connexion immédiate (même forme que /login).
 */
export function SetPasswordPage({ token }: { token: string }) {
  const [tokenInfo, setTokenInfo] = useState<{ email: string; tenantName: string } | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const setJwt        = useGameStore((s) => s.setJwt);
  const setAuthState  = useGameStore((s) => s.setAuthState);
  const setProfile    = useGameStore((s) => s.setProfile);
  const setAppearance = useGameStore((s) => s.setAppearance);
  const setTargetZoneSlug  = useGameStore((s) => s.setTargetZoneSlug);
  const setTargetSpawnName = useGameStore((s) => s.setTargetSpawnName);

  const passwordEvaluation = useMemo(
    () => (password ? evaluatePassword(password, tokenInfo ? [tokenInfo.email] : []) : null),
    [password, tokenInfo],
  );

  // Complète zxcvbn (dictionnaires figés par langue) avec le corpus réel
  // Have I Been Pwned — appel réseau, donc différé de 600ms après la
  // dernière frappe, et seulement une fois que zxcvbn accepte déjà le mot
  // de passe (inutile de vérifier ce que zxcvbn a déjà rejeté).
  const [pwnedStatus, setPwnedStatus] = useState<'idle' | 'checking' | 'pwned' | 'clean'>('idle');
  useEffect(() => {
    if (!passwordEvaluation?.ok) {
      setPwnedStatus('idle');
      return;
    }
    let cancelled = false;
    setPwnedStatus('checking');
    const timeoutId = setTimeout(() => {
      void checkPwnedPassword(password).then((result) => {
        if (!cancelled) setPwnedStatus(result.pwned ? 'pwned' : 'clean');
      });
    }, 600);
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [password, passwordEvaluation?.ok]);

  useEffect(() => {
    let cancelled = false;
    const tenantSlug = getTenantSlug();
    fetch(`${API_BASE}/auth/password-setup/${token}`, {
      headers: { 'X-Tenant-ID': tenantSlug },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(SETUP_ERROR_MESSAGES[body.error ?? ''] ?? 'Ce lien est invalide ou a expiré.');
        }
        return res.json() as Promise<{ email: string; tenantName: string }>;
      })
      .then((data) => { if (!cancelled) setTokenInfo(data); })
      .catch((err) => { if (!cancelled) setTokenError(err instanceof Error ? err.message : 'Erreur'); });
    return () => { cancelled = true; };
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (passwordEvaluation && !passwordEvaluation.ok) {
      setError(passwordEvaluation.errors[0] ?? 'Mot de passe trop faible.');
      return;
    }
    if (pwnedStatus === 'pwned') {
      setError(SETUP_ERROR_MESSAGES.PWNED_PASSWORD ?? 'Ce mot de passe apparaît dans des fuites de données connues.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      const tenantSlug = getTenantSlug();
      const res = await fetch(`${API_BASE}/auth/password-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantSlug,
        },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(SETUP_ERROR_MESSAGES[body.error ?? ''] ?? `Erreur ${res.status}`);
      }

      const data = await res.json() as { token: string; displayName?: string; avatarIndex?: number; appearance?: Record<string, unknown> | null };
      // Même logique que LoginPage.handleSubmit — reset la zone cible d'une
      // eventuelle session précédente dans le même onglet.
      setTargetZoneSlug(''); // carte d'arrivée du campus
      setTargetSpawnName(null);
      setJwt(data.token);
      const name = data.displayName ?? tokenInfo?.email.split('@')[0] ?? 'Anonyme';
      setProfile(name, data.avatarIndex ?? 0);
      if (data.appearance) {
        setAppearance(data.appearance as any);
      }
      const needsOnboarding = !data.displayName || data.displayName.trim() === '' || !data.appearance;
      setAuthState(needsOnboarding ? 'authenticated' : 'lobby');

      // Nettoie le token de l'URL — un lien à usage unique ne doit pas rester visible/rechargeable.
      window.history.replaceState({}, '', window.location.pathname);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }

  const disableSubmit = loading
    || (passwordEvaluation !== null && !passwordEvaluation.ok)
    || pwnedStatus === 'checking'
    || pwnedStatus === 'pwned';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100vw',
        height: '100dvh',
        background: 'var(--color-bg-base)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border-default)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-10) var(--space-8)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
            <LogoMark size={100} />
          </div>
          <h1 style={{
            margin: 0,
            color: 'var(--color-text-primary)',
            fontSize: 'var(--text-xl)',
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-bold)' as unknown as number,
            letterSpacing: 'var(--tracking-tight)',
          }}>
            Placio
          </h1>
          <p style={{
            margin: '6px 0 0',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)',
          }}>
            {tokenInfo ? `Crée ton mot de passe pour ${tokenInfo.tenantName}` : 'Création de mot de passe'}
          </p>
        </div>

        {tokenError && !tokenInfo && (
          <div
            style={{
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-danger-muted)',
              border: '1px solid rgba(240, 112, 112, 0.3)',
              color: 'var(--color-danger)',
              fontSize: 'var(--text-sm)',
              textAlign: 'center',
            }}
          >
            {tokenError}
          </div>
        )}

        {!tokenError && !tokenInfo && (
          <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
            Vérification du lien…
          </p>
        )}

        {tokenInfo && (
          <form onSubmit={(e) => { void handleSubmit(e); }} noValidate>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginTop: 0 }}>
              Compte : <strong>{tokenInfo.email}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div>
                <label style={{
                  display: 'block',
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--text-xs)',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 'var(--font-medium)' as unknown as number,
                  marginBottom: 'var(--space-2)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-wide)',
                }}>
                  Nouveau mot de passe
                </label>
                <p style={{
                  margin: '0 0 var(--space-3)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.5,
                }}>
                  {PASSWORD_MIN_LENGTH} caractères minimum. Pas besoin de majuscule/chiffre/symbole
                  obligatoire - préfère une phrase longue et facile à retenir plutôt qu'un mot de
                  passe court et complexe. Évite les mots de passe courants ou devinables.
                </p>
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  required
                  autoFocus
                  autoComplete="new-password"
                  style={inputStyle}
                />
                {passwordEvaluation && (
                  <div style={{ marginTop: 'var(--space-2)' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {STRENGTH_LABELS.map((_, i) => (
                        <div
                          key={i}
                          style={{
                            height: 4,
                            flex: 1,
                            borderRadius: 2,
                            background: i <= passwordEvaluation.score
                              ? STRENGTH_COLORS[passwordEvaluation.score]
                              : 'var(--color-border-default)',
                            transition: 'background var(--transition-base)',
                          }}
                        />
                      ))}
                    </div>
                    <p style={{
                      margin: '4px 0 0',
                      fontSize: 'var(--text-xs)',
                      color: passwordEvaluation.ok ? STRENGTH_COLORS[passwordEvaluation.score] : 'var(--color-danger)',
                    }}>
                      {passwordEvaluation.ok
                        ? STRENGTH_LABELS[passwordEvaluation.score]
                        : passwordEvaluation.errors[0]}
                    </p>
                    {passwordEvaluation.ok && pwnedStatus !== 'idle' && (
                      <p style={{
                        margin: '2px 0 0',
                        fontSize: 'var(--text-xs)',
                        color: pwnedStatus === 'pwned' ? 'var(--color-danger)' : 'var(--color-text-muted)',
                      }}>
                        {pwnedStatus === 'checking' && 'Vérification contre les fuites de données connues…'}
                        {pwnedStatus === 'pwned' && SETUP_ERROR_MESSAGES.PWNED_PASSWORD}
                        {pwnedStatus === 'clean' && '✓ Absent des fuites de données connues.'}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label style={{
                  display: 'block',
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--text-xs)',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 'var(--font-medium)' as unknown as number,
                  marginBottom: 'var(--space-2)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-wide)',
                }}>
                  Confirmer le mot de passe
                </label>
                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  required
                  autoComplete="new-password"
                  style={inputStyle}
                />
              </div>
            </div>

            {error && (
              <div
                style={{
                  marginTop: 'var(--space-3)',
                  padding: 'var(--space-3) var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-danger-muted)',
                  border: '1px solid rgba(240, 112, 112, 0.3)',
                  color: 'var(--color-danger)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={disableSubmit}
              style={{
                ...btnStyle,
                marginTop: 'var(--space-6)',
                opacity: disableSubmit ? 0.6 : 1,
              }}
            >
              {loading ? 'Connexion…' : 'Créer mon mot de passe et me connecter'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
